import * as _ from "underscore";

import { spanWordRegExp } from "./spannify.js";
import {
  cjkThai,
  letter as L,
  mark as M,
  notLetter,
} from "./unicodePatterns.js";

export const notWordRegExp = () => new RegExp(`[^’’${L}${M}]+`, "gu");

const cjkThaiRegExp = new RegExp(cjkThai, "u");
const combiningMarkRegExp = new RegExp(`[${M}]`, "u");

/**
 * Splits a saved phrase into the same tokens that spannify() wraps in
 * <span data-p> elements, so phrases can be matched against the spans rendered
 * in the reader.
 *
 * For languages without spaces between words (Chinese, Japanese, Thai, …) each
 * character becomes its own token — with combining marks kept attached to their
 * base — mirroring how spannify wraps these scripts character-by-character. This
 * is what lets multi-character saved words (e.g. "你好") match the individual
 * character spans in the document. For other languages each whole word is a
 * token, matching the previous notWordRegExp() behaviour.
 *
 * @param {string} phrase
 * @param {boolean} noWordBoundaries
 * @returns {string[]}
 */
export const splitPhraseIntoSpanTokens = (phrase, noWordBoundaries) => {
  const lower = phrase.toLowerCase();
  if (!noWordBoundaries) {
    return lower.split(notWordRegExp());
  }

  const tokens = [];
  for (const group of lower.match(spanWordRegExp()) || []) {
    if (!cjkThaiRegExp.test(group)) {
      tokens.push(group);
      continue;
    }
    for (let index = 0; index < group.length; index++) {
      let token = group[index];
      while (
        index < group.length - 1 &&
        combiningMarkRegExp.test(group[index + 1])
      ) {
        index++;
        token += group[index];
      }
      tokens.push(token);
    }
  }
  return tokens;
};

export const addPrePadding = (content, padding) => {
  const contentString = String(content);
  return (
    padding.substring(0, padding.length - contentString.length) + contentString
  );
};

export const elide = (string, maxLength) => {
  if (string.length > maxLength) {
    return string.substring(0, maxLength - 2) + "…";
  }
  return string;
};

export const regexpEscape = (input) =>
  input.replace(/[[\]/{}()*+?.\\^$|]/g, "\\$&");

export const stripDiacritics = (input) =>
  input.normalize("NFD").replace(/\p{Diacritic}/gu, "");

export const htmlEscape = (input) =>
  input
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

export const simpleMarkupToHtml = (input) => {
  if (!input) {
    return "";
  }
  return (
    htmlEscape(input)
      // bold
      .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
      // urls
      .replace(/https?:\/\/\S*/g, (url) => {
        // ignore trailing punctuation
        let after = "";
        while (
          url.length > 0 &&
          /[.,<>(){}\-;:'"[\]]/g.test(url[url.length - 1])
        ) {
          after += url[url.length - 1];
          url = url.substring(0, url.length - 1);
        }
        return '<a href="' + url + '">' + url + "</a>" + after;
      })
      // newlines
      .replace(/\n/g, "<br>")
  );
};

export const addCommasToNumber = (number) => {
  let string = String(number);
  const result = [];

  while (string.length > 0) {
    result.splice(
      0,
      0,
      string.slice(Math.max(string.length - 3, 0), string.length)
    );
    string = string.substring(0, Math.max(string.length - 3, 0));
  }

  return result.join(",");
};

export const getDomainFromUrl = (url) =>
  _.find(url.split("/"), (a) => a.length > 0 && !/:$/.test(a));

const normalizedCharacterMap = {
  // apostrophes
  "'": "'",
  "`": "'",
  "´": "'",
  ʹ: "'",
  ʻ: "'",
  ʼ: "'",
  ʽ: "'",
  ʾ: "'",
  ʿ: "'",
  ˈ: "'",
  ˊ: "'",
  ʹ: "'",
  "΄": "'",
  "׳": "'",
  "᾽": "'",
  "᾿": "'",
  "‘": "'",
  "’": "'",
  "‛": "'",
  "′": "'",
  "‵": "'",
  ꞌ: "'",
  "＇": "'",

  // hyphens
  "-": "-",
  "᠆": "-",
  "‐": "-",
  "‑": "-",
  "‒": "-",
  "–": "-",
  "—": "-",
  "―": "-",
  "−": "-",
  "﹘": "-",
  "﹣": "-",
  "－": "-",
};

export const normalizeString = (string, normalizeAccents) => {
  if (string.normalize) {
    string = string.normalize();
  }

  const characterMap = normalizeAccents
    ? {
        ...normalizedCharacterMap,
        ...accentsMap,
      }
    : normalizedCharacterMap;

  return string
    .split("")
    .map((character) => characterMap[character] ?? character)
    .join("");
};

// sorts an array in place, by an arbitrary number of sort keys
export const sortBy = (array, sortKeys) => {
  const result = _.clone(array);
  const keyPairs = _.pairs(sortKeys);

  result.sort((a, b) => {
    for (let i = 0; i < keyPairs.length; i++) {
      const key = keyPairs[i][0];
      const direction = keyPairs[i][1];

      if (!a[key] && b[key]) return direction;
      if (a[key] && !b[key]) return -direction;
      if (a[key] > b[key]) return direction;
      if (a[key] < b[key]) return -direction;
    }
    return 0;
  });

  return result;
};

// from http://en.wikibooks.org/wiki/Algorithm_Implementation/Strings/Levenshtein_distance#JavaScript
export const levenshteinDistance = (a, b) => {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const matrix = [];

  // increment along the first column of each row
  let i;
  for (i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }

  // increment each column in the first row
  let j;
  for (j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  // Fill in the rest of the matrix
  for (i = 1; i <= b.length; i++) {
    for (j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) == a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          Math.min(
            matrix[i][j - 1] + 1, // insertion
            matrix[i - 1][j] + 1
          )
        ); // deletion
      }
    }
  }

  return matrix[b.length][a.length];
};

export const childExists = (object, path) =>
  typeof valueFromPath(object, path) !== "undefined";

// returns undefined if path doesn't exist
export const valueFromPath = (object, path) => {
  if (!object) {
    return undefined;
  }

  let restOfObject = object;

  const exists = _.every(path.split("."), (key) => {
    if (restOfObject[key]) {
      restOfObject = restOfObject[key];
      return true;
    }
  });

  if (exists) {
    return restOfObject;
  }
};

export const toTitleCase = (input) =>
  input.replace(
    /(^|\W)(\w)/g,
    (_whole, space, letter) => space + letter.toUpperCase()
  );

/** @param {string[]} terms */
export const displayListOfTerms = (terms) =>
  terms
    .map((term, index) => {
      if (index === terms.length - 1) {
        return term;
      } else if (index === terms.length - 2) {
        return `${term} and`;
      } else {
        return `${term},`;
      }
    })
    .join(" ");

export const sleep = async (duration) =>
  new Promise((resolve) => {
    setTimeout(resolve, duration);
  });

/** A map of accented unicode characters to their ASCII equivalent */
const accentsMap = {
  á: "a",
  à: "a",
  â: "a",
  ä: "a",
  ã: "a",
  å: "a",
  ā: "a",
  ă: "a",
  ą: "a",
  ç: "c",
  ć: "c",
  č: "c",
  ĉ: "c",
  ċ: "c",
  ď: "d",
  đ: "d",
  é: "e",
  è: "e",
  ê: "e",
  ë: "e",
  ē: "e",
  ĕ: "e",
  ė: "e",
  ę: "e",
  ě: "e",
  ƒ: "f",
  ĝ: "g",
  ğ: "g",
  ġ: "g",
  ģ: "g",
  ĥ: "h",
  ħ: "h",
  í: "i",
  ì: "i",
  î: "i",
  ï: "i",
  ī: "i",
  ĭ: "i",
  į: "i",
  i̇: "i",
  ĵ: "j",
  ķ: "k",
  ĸ: "k",
  ĺ: "l",
  ļ: "l",
  ľ: "l",
  ŀ: "l",
  ł: "l",
  ń: "n",
  ņ: "n",
  ň: "n",
  ñ: "n",
  ó: "o",
  ò: "o",
  ô: "o",
  ö: "o",
  õ: "o",
  ø: "o",
  ō: "o",
  ŏ: "o",
  ő: "o",
  œ: "oe",
  ŕ: "r",
  ŗ: "r",
  ř: "r",
  ś: "s",
  š: "s",
  ş: "s",
  ŝ: "s",
  ș: "s",
  ţ: "t",
  ť: "t",
  ŧ: "t",
  ț: "t",
  ú: "u",
  ù: "u",
  û: "u",
  ü: "u",
  ũ: "u",
  ū: "u",
  ŭ: "u",
  ů: "u",
  ű: "u",
  ų: "u",
  ý: "y",
  ỳ: "y",
  ŷ: "y",
  ÿ: "y",
  ŵ: "w",
  ź: "z",
  ž: "z",
  ż: "z",
  þ: "th",
  ß: "ss",
  æ: "ae",
  ǽ: "ae",

  // Possible accented characters in Greek alphabet
  // Greek alphabet accented characters
  ά: "α",
  έ: "ε",
  ή: "η",
  ί: "ι",
  ϊ: "ι",
  ΐ: "ι",
  ό: "ο",
  ύ: "υ",
  ϋ: "υ",
  ΰ: "υ",
  ώ: "ω",
  Ά: "Α",
  Έ: "Ε",
  Ή: "Η",
  Ί: "Ι",
  Ϊ: "Ι",
  Ό: "Ο",
  Ύ: "Υ",
  Ϋ: "Υ",
  Ώ: "Ω",

  // Russian alphabet accented characters
  а́: "а",
  е́: "е",
  и́: "и",
  о́: "о",
  у́: "у",
  ы́: "ы",
  э́: "э",
  ю́: "ю",
  я́: "я",
  ё: "е",

  // Other Cyrillic characters with diacritics
  ў: "у",
  ѝ: "и",
  ѐ: "е",
  ѓ: "г",
  ќ: "к",
  ї: "і",
  ђ: "ћ",
  ћ: "ч",
  ӂ: "ж",
  ӣ: "и",
  ӯ: "у",
  ӱ: "у",
  ӳ: "у",
  ӵ: "ч",
  ӹ: "ы",
};

export const normalizeAccents = (input) => {
  return input
    .split("")
    .map((char) => accentsMap[char] || char)
    .join("");
};

/**
 * For formatting conditional class names
 *
 * @param {Record<string, boolean>} classNames
 * @returns {string}
 */
export const cn = (classNames) =>
  Object.entries(classNames)
    .filter(([, value]) => value)
    .map(([key]) => key)
    .join(" ");

/**
 * Returns a deep clone of the given object.
 *
 * XXX This could be made more efficient by avoiding conversion to JSON.
 */
export const deepClone = (object) => JSON.parse(JSON.stringify(object));

export const mulberry32 = (seed) => {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

/** Splits the given `input` text into an array of sentences */
const splitIntoSentences = (input) => {
  const parts = input.split(/([.!?])\s+/);
  const sentences = [];

  for (let i = 0; i < parts.length; i += 2) {
    const sentence = parts[i] + (parts[i + 1] || "");
    sentences.push(sentence.trim());
  }

  return sentences;
};

/**
 * Returns the first sentence in the text that contains the word or phrase.
 */
export const findContextSentence = (text, wordOrPhrase) => {
  const sentences = splitIntoSentences(text);
  const regex = new RegExp(
    `(^|${notLetter})${regexpEscape(wordOrPhrase)}(?=${notLetter}|$)`,
    "iu"
  );

  for (const sentence of sentences) {
    if (regex.test(sentence)) {
      return sentence.trim();
    }
  }

  // Perhaps the word or phrase contains a sentence boundary character
  // (e.g., a period). So look for it in the entire text and if found, return the sentences either
  // side of the word or phrase.
  const match = text.match(regex);
  if (match) {
    const before = text.slice(0, match.index);
    const after = text.slice(match.index + match[0].length);

    const sentenceBefore = trimStart(splitIntoSentences(before).pop());
    const sentenceAfter = trimEnd(splitIntoSentences(after).shift());

    return sentenceBefore + match[0] + sentenceAfter;
  }

  return null;
};

/** Replacement for String.trimStart() but compatible with older browsers */
export const trimStart = (string) => string.replace(/^\s+/, "");

/** Replacement for String.trimEnd() but compatible with older browsers */
export const trimEnd = (string) => string.replace(/\s+$/, "");

export const filterOutEmojis = (text) =>
  text.replace(
    // eslint-disable-next-line no-misleading-character-class
    /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F1E6}-\u{1F1FF}\u{1F191}-\u{1F251}\u{1F004}\u{1F0CF}\u{1F018}-\u{1F270}\u{238C}-\u{2454}\u{20D0}-\u{20FF}]/gu,
    ""
  );

/**
 * Returns "a" or "an" depending on the given number.
 */
export const aOrAn = (numeral) => {
  // Convert number to string to check the first digit/character
  const numStr = String(numeral);

  // Numbers that start with vowel sounds when spoken
  // 8 (eight), 11 (eleven), 18 (eighteen), etc.
  const vowelSoundStarters = [
    "8",
    "11",
    "18",
    "80",
    "81",
    "82",
    "83",
    "84",
    "85",
    "86",
    "87",
    "88",
    "89",
  ];

  // Check if the number starts with a vowel sound
  if (vowelSoundStarters.some((starter) => numStr.startsWith(starter))) {
    return "an";
  }

  return "a";
};
